import React, { useState, useEffect, useCallback } from "react";
import { getBankSlipsAPI } from "../../services/portal/admin/bankTransferService";
import { showErrorToastMsg } from "../../utility/ToastMessage";
import Loader from "../Loader";
import { formatAmount, getMonthNameFromDate } from "../../utility/Common";
import { getMyBankSlipsAPI } from "../../services/portal/student/bankTransferService";

const StudentPaymentHistory = ({ student, isAdminView = false }) => {
  const [loading, setLoading] = useState(false);
  const [filteredBankTransfers, setFilteredBankTransfers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("1");

  const loadBankTransfers = useCallback(async () => {
    setLoading(true);
    try {
      var res;
      if (isAdminView) {
        res = await getBankSlipsAPI(student.UserId, selectedStatus);
      } else {
        res = await getMyBankSlipsAPI(0, selectedStatus, "''");
      }
      if (res && res.IsSuccessful && res.Data) {
        setFilteredBankTransfers(res.Data || []);
      } else {
        setFilteredBankTransfers([]);
      }
    } catch (error) {
      console.error("Error loading bank transfers:", error);
      showErrorToastMsg("Failed to load bank transfers");
      setFilteredBankTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus]);

  useEffect(() => {
    loadBankTransfers();
  }, [loadBankTransfers]);

  const handleStatusFilterChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const getStatusBadge = (status) => {
    const statusNum = Number(status);
    if (statusNum === 2) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          Approved
        </span>
      );
    } else if (statusNum === 4) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Rejected
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
  };

  return (
    <div>
      {/* Filter Section */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Status:
            </label>
            <select
              value={selectedStatus}
              onChange={handleStatusFilterChange}
              className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
            >
              <option value="1">Pending</option>
              <option value="2">Approved</option>
              <option value="4">Rejected</option>
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {filteredBankTransfers.length} transfer(s)
          </div>
        </div>
      </div>

      {/* Bank Transfers Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-br from-black to-amber-500 px-6 py-4 border-b border-gray-200">
          <h4 className="text-md font-medium text-white">
            Bank Transfers List | ගෙවීම් විස්තර
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipt Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                  >
                    <Loader />
                  </td>
                </tr>
              ) : filteredBankTransfers.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                  >
                    No bank transfers found.
                  </td>
                </tr>
              ) : (
                filteredBankTransfers.map((transfer) => {
                  return (
                    <tr key={transfer.BankSlipId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {transfer.User?.FirstName +
                          " " +
                          transfer.User?.LastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transfer.Class?.Name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transfer.Date
                          ? getMonthNameFromDate(transfer.Date)
                          : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {transfer.IsFreePass ? "Free Card" : (transfer.Amount ? formatAmount(transfer.Amount) : "")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transfer.Number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {getStatusBadge(transfer.Status)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudentPaymentHistory;
